import { analyticsService, AnalyticsEventCatalog } from "../index";

/**
 * @param {string} format
 * @returns {"round_robin"|"knockout"|"qualifier"}
 */
export function mapCompetitionFormat(format) {
  if (format === "round-robin") return "round_robin";
  if (format === "qualifier") return "qualifier";
  return "knockout";
}

/** @deprecated use mapCompetitionFormat */
export const mapTournamentFormat = mapCompetitionFormat;

/**
 * @param {object | null | undefined} t
 */
export function countCompetitionEntrants(t) {
  if (!t) return 0;
  if (Array.isArray(t.participants) && t.participants.length) {
    return t.participants.length;
  }
  if (Array.isArray(t.entries) && t.entries.length) {
    return t.entries.length;
  }
  if (Array.isArray(t.teamOrder)) return t.teamOrder.length;
  return 0;
}

/** @deprecated */
export const countTournamentEntrants = countCompetitionEntrants;

function collectPlayableMatches(t) {
  if (!t) return [];
  const root = t.groupsDone && t.knockout ? t.knockout : t;
  const rounds = root.rounds || [];
  return rounds.flatMap((r) => r.matches || []).filter((m) => !m.isBye && m.status !== "bye");
}

/**
 * @param {object | null | undefined} t
 */
export function isCompetitionBracketComplete(t) {
  if (!t?.started) return false;
  const matches = collectPlayableMatches(t);
  if (!matches.length) return false;
  return matches.every((m) => m.status === "done");
}

/** @deprecated */
export const isTournamentBracketComplete = isCompetitionBracketComplete;

/**
 * @param {object | null | undefined} t
 */
export function countCompetitionMatchesPlayed(t) {
  if (!t) return 0;
  const root =
    t.groupsDone && t.knockout
      ? { rounds: [...(t.rounds || []), ...(t.knockout.rounds || [])] }
      : t;
  const rounds = root.rounds || [];
  return rounds
    .flatMap((r) => r.matches || [])
    .filter((m) => m.status === "done" && !m.isBye).length;
}

/** @deprecated */
export const countTournamentMatchesPlayed = countCompetitionMatchesPlayed;

/**
 * @param {object | null | undefined} t
 * @returns {"side_a"|"side_b"|"n/a"}
 */
export function resolveCompetitionWinnerSide(t) {
  if (!t) return "n/a";
  const root = t.groupsDone && t.knockout ? t.knockout : t;
  const rounds = root.rounds || [];
  const last = rounds[rounds.length - 1];
  const final = last?.matches?.[0];
  if (!final?.winner) return "n/a";
  if (final.winner === final.teamA) return "side_a";
  if (final.winner === final.teamB) return "side_b";
  return "n/a";
}

/** @deprecated */
export const resolveTournamentWinnerSide = resolveCompetitionWinnerSide;

/**
 * @param {{ format: string; entrantCount?: number }} params
 */
export function trackCompetitionCreated(params) {
  analyticsService.track(AnalyticsEventCatalog.COMPETITION_CREATED, {
    competition_format: mapCompetitionFormat(params.format),
    ...(typeof params.entrantCount === "number"
      ? { entrant_count: params.entrantCount }
      : {}),
  });
}

/**
 * @param {{ format: string; entrantCount?: number }} params
 */
export function trackCompetitionPublished(params) {
  analyticsService.track(AnalyticsEventCatalog.COMPETITION_PUBLISHED, {
    competition_format: mapCompetitionFormat(params.format),
    ...(typeof params.entrantCount === "number"
      ? { entrant_count: params.entrantCount }
      : {}),
  });
}

/**
 * @param {{ format: string; entrantCount?: number }} params
 */
export function trackCompetitionStarted(params) {
  analyticsService.track(AnalyticsEventCatalog.COMPETITION_STARTED, {
    competition_format: mapCompetitionFormat(params.format),
    ...(typeof params.entrantCount === "number"
      ? { entrant_count: params.entrantCount }
      : {}),
  });
}

/**
 * @param {{
 *   format: string;
 *   matchesPlayed?: number;
 *   winnerSide?: "side_a"|"side_b"|"n/a";
 * }} params
 */
export function trackCompetitionCompleted(params) {
  const payload = {
    competition_format: mapCompetitionFormat(params.format),
  };
  if (typeof params.matchesPlayed === "number" && params.matchesPlayed >= 0) {
    payload.matches_played = params.matchesPlayed;
  }
  if (params.winnerSide) {
    payload.winner_side = params.winnerSide;
  }
  analyticsService.track(AnalyticsEventCatalog.COMPETITION_COMPLETED, payload);
}

/**
 * After Competition save — emit completion once when bracket becomes fully done.
 * @param {object | null | undefined} previous
 * @param {object | null | undefined} next
 * @param {{ completedFiredRef: { current: boolean } }} ctx
 */
export function trackCompetitionOnPersist(previous, next, ctx) {
  if (!next?.started || ctx.completedFiredRef.current) return;
  const wasComplete = isCompetitionBracketComplete(previous);
  const isComplete = isCompetitionBracketComplete(next);
  if (!wasComplete && isComplete) {
    trackCompetitionCompleted({
      format: next.type || "knockout",
      matchesPlayed: countCompetitionMatchesPlayed(next),
      winnerSide: resolveCompetitionWinnerSide(next),
    });
    ctx.completedFiredRef.current = true;
  }
}

/** @deprecated */
export const trackTournamentCreated = trackCompetitionCreated;
/** @deprecated */
export const trackTournamentStarted = trackCompetitionStarted;
/** @deprecated */
export const trackTournamentCompleted = trackCompetitionCompleted;
/** @deprecated */
export const trackTournamentOnPersist = trackCompetitionOnPersist;
